// This config covers two fundamentally different products under one PGD —
// Vivotif® (oral live attenuated) and TYPHIM Vi® (injectable inactivated Vi
// polysaccharide). Schedule selection is labelled by brand. The pharmacist
// selects the correct schedule for their chosen brand. ScheduleOption has no
// brandId field to filter by — this is a known limitation, not to be fixed
// here (see tier2Config.types.ts).

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.TYPHOID,
  displayName: "Typhoid",
  serviceType: "administration",
  minimumAgeYears: 2,
  indicationSummary:
    "Travelling to or at risk in a typhoid-endemic area as identified by NaTHNaC assessment. Vivotif\u00ae (oral) for ages 5 and over; TYPHIM Vi\u00ae (injectable) for ages 2 and over.",

  brands: [
    {
      brandId: "typhim-vi",
      brandName: "TYPHIM Vi\u00ae (injectable)",
      minimumAgeYears: 2,
      route: "IM",
      doseDescription: "25 \u00b5g Vi polysaccharide / 0.5 mL",
      administrationNotes:
        "Shake the syringe to obtain a clear, colourless to slightly yellow solution. Inspect before use. Administer by intramuscular injection into the deltoid muscle. May be given by deep subcutaneous injection in patients with bleeding disorders.",
    },
    {
      brandId: "vivotif",
      brandName: "Vivotif\u00ae (oral capsules)",
      minimumAgeYears: 5,
      route: "oral",
      doseDescription:
        "One capsule per dose; 3 doses on alternate days (days 1, 3, and 5)",
      administrationNotes:
        "Swallow capsules whole \u2014 do not open or chew. Take on an empty stomach at least 1 hour before a meal, with cool water only (not warm or hot). Capsules must be stored refrigerated at 2\u20138\u00b0C and brought to room temperature before swallowing. The first dose is taken under supervision in the pharmacy. Doses 2 and 3 are supplied for self-administration with written storage and handling instructions.",
    },
  ],

  schedules: [
    {
      scheduleId: "typhoid-typhimvi-primary",
      label:
        "TYPHIM Vi\u00ae \u2014 single dose (primary), at least 2 weeks before exposure",
      offLabel: false,
      minimumAgeYears: 2,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 single primary dose",
        },
      ],
      boosterInfo:
        "A booster dose of TYPHIM Vi\u00ae is recommended every 3 years for patients at continued risk.",
      completionNotes:
        "Ideally given at least 2 weeks before potential exposure. May be given up to the day of departure if required.",
    },
    {
      scheduleId: "typhoid-vivotif-primary",
      label:
        "Vivotif\u00ae \u2014 primary course (3 capsules on days 1, 3, and 5)",
      offLabel: false,
      minimumAgeYears: 5,
      doses: [
        {
          doseNumber: 1,
          label: "Day 1 \u2014 first capsule (taken in pharmacy under supervision)",
        },
        {
          doseNumber: 2,
          label: "Day 3 \u2014 second capsule (self-administered)",
          intervalFromDose1Days: 2,
        },
        {
          doseNumber: 3,
          label: "Day 5 \u2014 third capsule (self-administered)",
          intervalFromDose1Days: 4,
        },
      ],
      boosterInfo:
        "A booster course of 3 capsules (days 1, 3, and 5) is recommended every 3 years for patients at continued risk.",
      completionNotes:
        "The complete 3-capsule course must be finished at least 1 week before potential exposure. Supply capsules 2 and 3 to the patient with refrigerator storage instructions and written dosing instructions for self-administration.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "typhoid-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to any typhoid vaccine or to any of their components?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Typhoid vaccine is contraindicated in this patient. Do not proceed.",
      referralMessage: "Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "typhoid-ex-formaldehyde-casein",
      question:
        "Does the patient have known hypersensitivity to formaldehyde or casein? (Relevant only if TYPHIM Vi\u00ae has been selected \u2014 this product contains formaldehyde and casein residuals.)",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "TYPHIM Vi\u00ae is contraindicated in patients with hypersensitivity to formaldehyde or casein. If Vivotif\u00ae was selected and the patient is aged 5 or over, consider switching \u2014 Vivotif\u00ae does not contain these residuals.",
      referralMessage:
        "If no suitable alternative is available, refer to the patient's GP.",
    },
  ],

  cautions: [
    {
      checkId: "typhoid-caut-bleeding",
      question:
        "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy? (Relevant if TYPHIM Vi\u00ae has been selected.)",
      cautionMessage:
        "TYPHIM Vi\u00ae should be administered with care by intramuscular injection using a fine-gauge needle. For patients on stable warfarin with INR within therapeutic range, IM injection may proceed with firm pressure for at least two minutes. TYPHIM Vi\u00ae may also be given by deep subcutaneous injection in patients where IM injection is contraindicated. Document the clinical decision. Note: Vivotif\u00ae is oral and is not affected by bleeding disorders.",
    },
    {
      checkId: "typhoid-caut-immunosuppressed-typhimvi",
      question:
        "Is the patient immunosuppressed, asplenic, or living with HIV? (Relevant if TYPHIM Vi\u00ae has been selected \u2014 response may be reduced.)",
      cautionMessage:
        "Immunosuppression, asplenia, and HIV may reduce the immune response to TYPHIM Vi\u00ae. Vaccination should proceed as the vaccine is inactivated and safe. Counsel that protection may be suboptimal. For HIV-positive patients, specialist advice is recommended. Note: Vivotif\u00ae is contraindicated in immunosuppressed patients \u2014 see exclusion check above.",
    },
    {
      checkId: "typhoid-caut-pregnancy",
      question: "Is the patient pregnant or considering pregnancy?",
      cautionMessage:
        "Vivotif\u00ae is a live vaccine and should not be used in pregnancy. If typhoid vaccination is indicated in a pregnant patient, use TYPHIM Vi\u00ae (inactivated, injectable) with a documented risk-benefit discussion. Advise the patient to inform their GP or midwife.",
    },
    {
      checkId: "typhoid-caut-vivotif-sugars",
      question:
        "Has the patient been diagnosed with sucrase-isomaltase insufficiency, congenital sucrose-isomaltase deficiency, or glucose-galactose malabsorption? (Relevant if Vivotif\u00ae has been selected \u2014 capsules contain lactose and sucrose.)",
      cautionMessage:
        "Vivotif\u00ae capsules contain lactose and sucrose and are unsuitable for patients with rare hereditary sugar disorders. If Vivotif\u00ae was selected, switch to TYPHIM Vi\u00ae (injectable) if the patient is aged 2 or over.",
    },
    {
      checkId: "typhoid-caut-syncope",
      question:
        "Is there any history of syncope (fainting) following a previous injection or vaccination? (Relevant if TYPHIM Vi\u00ae has been selected.)",
      cautionMessage:
        "Syncope can occur following injection. Ensure the patient is seated or lying down during and for the duration of the observation period after TYPHIM Vi\u00ae administration. Note: Vivotif\u00ae is oral and does not carry syncope risk from injection.",
    },
  ],

  supplyGates: [],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against typhoid fever (Salmonella Typhi) only. It does not protect against paratyphoid fever or other causes of enteric fever.",
    "If you received Vivotif\u00ae (oral capsules): keep the remaining capsules in the refrigerator at 2\u20138\u00b0C. Take each capsule on an empty stomach at least 1 hour before food, swallowing it whole with cool water. Take one capsule on day 3 and one on day 5. Complete the full 3-capsule course at least 1 week before travel.",
    "If you received TYPHIM Vi\u00ae (injection): a single injection provides protection. Ideally given at least 2 weeks before exposure.",
    "A booster is recommended every 3 years if you remain at risk.",
    "Typhoid vaccination reduces but does not eliminate the risk of infection. Continue to follow strict food and water hygiene: drink bottled or boiled water, avoid ice in drinks, eat freshly cooked food, and wash hands before eating and after using the toilet.",
    "If you develop a high or persistent fever, severe headache, stomach pain, or constipation or diarrhoea during or after travel, seek medical attention promptly. Tell the doctor you have been in a typhoid-risk area.",
  ],
});
